import React, { useCallback } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';

import { Button, Stack, TextField, Tooltip } from '@mui/material';

import { useBlockOffers } from '~/hooks/apis/public-offer-feed/useOfferBlocklist';

import { OfferBlocklistBrand } from '~/services/PublicOfferFeedService';

interface FormValues {
  offerIds: string;
}

interface Props {
  brand: OfferBlocklistBrand;
}

export default function AddToBlocklistForm({ brand }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { control, handleSubmit, reset } = useForm<FormValues>({ defaultValues: { offerIds: '' } });
  const blockOffersMutation = useBlockOffers(brand);

  const onSubmit = useCallback(
    async ({ offerIds }: FormValues) => {
      const ids = offerIds
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      if (!ids.length) {
        enqueueSnackbar('Please enter at least one offer ID', { variant: 'error' });
        return;
      }

      try {
        await blockOffersMutation.mutateAsync(ids);
        reset({ offerIds: '' });
        enqueueSnackbar("Offers blocked successfully. They won't be participated in Ads", { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(`Failed to block offers: ${error.message ?? 'unknown error'}`, { variant: 'error' });
      }
    },
    [blockOffersMutation, enqueueSnackbar, reset],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Controller
          name="offerIds"
          control={control}
          rules={{ required: 'Offer ID is required' }}
          render={({ field, fieldState }) => (
            <Tooltip title="Multiple offer Id(s) must be comma separated.">
              <TextField
                {...field}
                fullWidth
                required
                label="Add OfferId(s) to Block"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            </Tooltip>
          )}
        />
        <Button type="submit" variant="contained" disabled={blockOffersMutation.isPending}>
          {blockOffersMutation.isPending ? 'Blocking...' : 'Submit'}
        </Button>
      </Stack>
    </form>
  );
}
